"use client";
import React, { useRef } from "react";
import { motion, useInView } from "motion/react";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import Image from "next/image";
import PageCta from "@/components/shared/page-cta";
import { useTranslations } from "next-intl";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const AboutTeam = () => {
  const t = useTranslations("about");
  const growthItems = [
    t("team.growItems.0"),
    t("team.growItems.1"),
    t("team.growItems.2"),
    t("team.growItems.3"),
  ];
  const whoWeWorkWith = [
    { title: t("team.clientTypes.0.title"), description: t("team.clientTypes.0.description") },
    { title: t("team.clientTypes.1.title"), description: t("team.clientTypes.1.description") },
    { title: t("team.clientTypes.2.title"), description: t("team.clientTypes.2.description") },
    { title: t("team.clientTypes.3.title"), description: t("team.clientTypes.3.description") },
  ];
  const growthRef = useRef<HTMLDivElement>(null);
  const whoRef = useRef<HTMLDivElement>(null);
  const growthInView = useInView(growthRef, { once: true, margin: "0px 0px -80px 0px" });
  const whoInView = useInView(whoRef, { once: true, margin: "0px 0px -80px 0px" });

  return (
    <>
      {/* ── HOW WE GROW ── */}
      <section className="section pb-[60px] lg:pb-[140px] relative">
        <div
          className="absolute left-[0%] top-[10%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.07)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none animate-shape-pulse"
          />

        <div className="container relative z-10">
          <motion.div
            ref={growthRef}
            variants={containerVariants}
            initial="hidden"
            animate={growthInView ? "show" : "hidden"}
            className="grid lg:grid-cols-2 gap-[40px] items-stretch"
          >
            {/* Left: heading + list */}
            <motion.div variants={itemVariants}>
              <h2 className="text-[28px] md:text-[40px] lg:text-[44px] font-semibold leading-[1.1] mb-[20px]">
                {t("team.growTitle")}
              </h2>
              <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 mb-[32px]">
                {t("team.growDescription")}
              </p>

              <ul className="space-y-[10px]">
                {growthItems.map((item, i) => (
                  <li key={i}>
                    <AnimatedCard
                      className="rounded-[14px]"
                      circleClassName="w-[80px] h-[80px] blur-[50px]"
                      animationDelay={i * 0.06}
                    >
                      <BorderWrapper
                        wrapperClass="rounded-[14px] bg-[linear-gradient(90deg,_#121124_0%,_#353353_100%)]"
                        className="rounded-[14px] bg-gradient-to-b from-[#121124] to-[#0e0821] px-[20px] py-[16px] flex items-center gap-[14px]"
                      >
                        <Image
                          src="/assets/illustrations/flyingcode-arrow.svg"
                          alt="Flyingcode arrow"
                          aria-hidden="true"
                          width={23}
                          height={15}
                          className="w-[23px] h-auto opacity-80 flex-shrink-0"
                        />
                        <span className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                          {item}
                        </span>
                      </BorderWrapper>
                    </AnimatedCard>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Right: quote card */}
            <motion.div variants={itemVariants} className="h-full">
              <AnimatedCard
                className="rounded-[18px] lg:rounded-[22px] h-full"
                circleClassName="w-[150px] h-[150px] blur-[90px]"
              >
                <BorderWrapper
                  wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                  className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[32px] lg:p-[44px] relative overflow-hidden"
                >
                  <div
                    className="absolute top-0 right-0 w-[280px] h-[280px] bg-[linear-gradient(180deg,_rgba(150,128,197,0.18)_0%,_rgba(98,42,173,0.1)_50%,_rgba(34,12,81,0)_100%)] rounded-full blur-[60px] pointer-events-none animate-shape-pulse"
                    />

                  {/* Large quote mark */}
                  <div className="text-[140px] leading-none font-serif text-theme/15 absolute top-[10px] right-[24px] select-none">&ldquo;</div>

                  <div className="relative z-10 flex flex-col h-full justify-between">
                    <div>
                      <div className="w-[36px] h-[2px] rounded-full bg-gradient-to-r from-theme to-[#622aad] mb-[28px]" />
                      <blockquote className="text-[24px] lg:text-[30px] font-semibold leading-[1.35] text-foreground/90">
                        {t("team.quote")}
                      </blockquote>
                      <p className="mt-[16px] text-[12px] font-light text-foreground/50 tracking-[0.15em] uppercase">
                        {t("team.quoteAttribution")}
                      </p>
                    </div>

                    <p className="mt-[40px] text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                      {t("team.quoteDescription")}
                    </p>
                  </div>
                </BorderWrapper>
              </AnimatedCard>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── WHO WE WORK WITH ── */}
      <section ref={whoRef} className="section pb-[60px] lg:pb-[140px] relative">
        <div
          className="absolute right-[-5%] top-[20%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(150,128,197,0.08)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[100px] pointer-events-none animate-shape-pulse"
          />

        <div className="container relative z-10">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={whoInView ? "show" : "hidden"}
          >
            <motion.div className="text-center mb-[50px] lg:mb-[70px]" variants={itemVariants}>
              <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1] mb-[16px]">
                {t("team.clientsTitle")}
              </h2>
              <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[500px] mx-auto">
                {t("team.clientsDescription")}
              </p>
            </motion.div>

            {/* MOBILE CAROUSEL */}
            <div className="md:hidden">
              <Carousel aria-label={t("team.clientsTitle")} opts={{ align: "center", loop: false }} className="w-full [&>div]:!overflow-visible">
                <CarouselContent className="-ml-2">
                  {whoWeWorkWith.map((item, i) => (
                    <CarouselItem key={i} className="pl-2 basis-[85%]">
                      <AnimatedCard
                        className="rounded-[18px] lg:rounded-[22px] h-full"
                        circleClassName="w-[120px] h-[120px] blur-[70px]"
                        animationDelay={i * 0.07}
                      >
                        <BorderWrapper
                          wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                          className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] relative overflow-hidden"
                        >
                          <div
                            className="absolute top-0 right-0 w-[120px] h-[120px] bg-theme/8 rounded-full blur-[50px] pointer-events-none animate-shape-pulse"
                            />
                          <div className="w-[32px] h-[3px] rounded-full bg-gradient-to-r from-theme to-[#622aad] mb-[18px]" />
                          <h3 className="text-[17px] lg:text-[19px] font-semibold leading-[1.3] mb-[10px]">
                            {item.title}
                          </h3>
                          <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                            {item.description}
                          </p>
                        </BorderWrapper>
                      </AnimatedCard>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <div className="flex justify-center items-center mt-[30px] gap-[15px]">
                  <CarouselPrevious className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
                  <CarouselNext className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
                </div>
              </Carousel>
            </div>
            {/* DESKTOP GRID */}
            <div className="hidden md:grid md:grid-cols-2 gap-[14px]">
              {whoWeWorkWith.map((item, i) => (
                <motion.div key={i} variants={itemVariants}>
                  <AnimatedCard
                    className="rounded-[18px] lg:rounded-[22px] h-full"
                    circleClassName="w-[120px] h-[120px] blur-[70px]"
                    animationDelay={i * 0.07}
                  >
                    <BorderWrapper
                      wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                      className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] relative overflow-hidden"
                    >
                      <div
                        className="absolute top-0 right-0 w-[120px] h-[120px] bg-theme/8 rounded-full blur-[50px] pointer-events-none animate-shape-pulse"
                        />
                      <div className="w-[32px] h-[3px] rounded-full bg-gradient-to-r from-theme to-[#622aad] mb-[18px]" />
                      <h3 className="text-[17px] lg:text-[19px] font-semibold leading-[1.3] mb-[10px]">
                        {item.title}
                      </h3>
                      <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90">
                        {item.description}
                      </p>
                    </BorderWrapper>
                  </AnimatedCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <PageCta
        title={t("cta.title")}
        description={t("cta.description")}
        primaryLabel={t("cta.button")}
      />
    </>
  );
};

export default AboutTeam;
